import React, { useRef } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { useElementDimensions } from "@sportal/cdk/hooks";

import {
  isBarchartLoadFailed,
  isBarchartLoading,
  getBarchartReports,
  isBarchartLoadSuccessful,
} from "../../../../store/reports/reducers/barchart/barchart.selectors";
import { getDrilldownReportsPeriod } from "../../drilldown.selectors";
import { NotMobile } from "../../../../hocs/Responsive";
import {
  BarChartProvider,
  BarChartTooltip,
  Bars,
  Brush,
  ChartNoDataMessage,
  useBarChartContext,
  useBarchartTooltipCleanup,
  XAxis,
  yPowScale,
} from "../../../../components/charts/barChart";
import {
  LocalLoader,
  LocalLoaderWrapper,
} from "../../../../components/loader/localLoader/LocalLoader";
import { LoadError } from "../../../../components/charts/LoadError";

import "./DrilldownBarchart.scss";

const BOTTOM_CHART_HEIGHT = 30;

const getChartSettings = (page) => ({
  barTypes: [page],
  dataSelection: {
    top: (d) => d[page],
  },
  [page]: {
    yOffsets: (val) => Math.abs(yPowScale(val)) || 0,
    getHeight: (val) => Math.abs(yPowScale(val) - yPowScale(0)) || 0,
  },
});

export const DrilldownBarchart = ({ page, onRefreshClick }) => {
  const container = useRef(null);
  const rawData = useSelector(getBarchartReports);
  const period = useSelector(getDrilldownReportsPeriod);
  const isLoading = useSelector(isBarchartLoading);
  const isFailure = useSelector(isBarchartLoadFailed);
  const { width, height } = useElementDimensions(container);

  useBarchartTooltipCleanup();

  return (
    <LocalLoaderWrapper>
      <div className="drilldown-barchart barchart">
        <div className="width-container">
          <div className="barchart-container" ref={container}>
            {isLoading && <LocalLoader />}
            {isFailure && (
              <LoadError
                onRefreshClick={onRefreshClick}
                className={"bar-chart-failure-container"}
                style={{ height: `calc(100% - ${BOTTOM_CHART_HEIGHT}px)` }}
              />
            )}
            <BarChartProvider
              chartWidth={width}
              chartHeight={height}
              bottomChartHeight={BOTTOM_CHART_HEIGHT}
              chartSettings={getChartSettings(page)}
              period={period}
              data={rawData}
            >
              <BarChart container={container} page={page} />
            </BarChartProvider>
          </div>
        </div>
      </div>
    </LocalLoaderWrapper>
  );
};

const BarChart = ({ container, page }) => {
  const { data } = useBarChartContext();
  const isSuccess = useSelector(isBarchartLoadSuccessful);

  const labelId = "drilldownBarChartLabelId";

  return (
    <div>
      <svg role="img" aria-describedby={labelId} focusable="false">
        <title>
          <FormattedMessage id={`drilldown_bar_chart_title_${page}`} />
        </title>
        <XAxis />
        {isSuccess &&
          (!_.isEmpty(data) ? (
            <Brush>
              <Bars data={data} type={page} />
            </Brush>
          ) : (
            <ChartNoDataMessage />
          ))}
      </svg>
      <NotMobile>
        <BarChartTooltip container={container} />
      </NotMobile>

      <div aria-hidden={true} className="sr-only" id={labelId}>
        <FormattedMessage id={`drilldown_bar_chart_label_${page}`} />
      </div>
    </div>
  );
};
